import { useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DocumentFileSection } from "@/components/documents/DocumentFileSection";
import { useCreateDocument, useAttachFile } from "@/hooks/useDocuments";
import { getDocumentFormSchema, type DocumentFormSchemaType, DocumentFormInput } from "@/schemas/documentSchema";
import {
  CONFIDENTIALITY_LEVELS,
  DOCUMENT_CATEGORIES,
  DISPLAY_TARGETS,
} from "@/types/document";
import {
  CONFIDENTIALITY_LABELS,
  DISPLAY_TARGET_LABELS,
  DOCUMENT_CATEGORY_LABELS,
} from "./documentLabels";

interface UploadedFile {
  fileId: string;
  fileName: string;
  previewUrl?: string;
}

interface CreateDocumentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateDocumentSheet({ open, onOpenChange }: CreateDocumentSheetProps) {
  const { t, i18n } = useLingui();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n object is stable; only locale determines schema output
  const documentSchema = useMemo(() => getDocumentFormSchema(i18n), [i18n.locale]);

  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  const form = useForm<DocumentFormInput, unknown, DocumentFormSchemaType>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      name: "",
      description: "",
      confidentiality: "public",
      category: "",
      is_published: false,
      display_targets: [],
    },
  });

  const createMutation = useCreateDocument();
  // Silent: the create toast reports the whole save, file included.
  const attachFileMutation = useAttachFile({ silent: true });

  const handleClose = () => {
    form.reset();
    if (uploadedFile?.previewUrl) {
      URL.revokeObjectURL(uploadedFile.previewUrl);
    }
    setUploadedFile(null);
    onOpenChange(false);
  };

  const handleFileRemoved = useCallback(() => {
    setUploadedFile(null);
  }, []);

  const onSubmit = async (values: DocumentFormSchemaType) => {
    const doc = await createMutation.mutateAsync({
      name: values.name,
      description: values.description || undefined,
      confidentiality: values.confidentiality,
      category: values.category,
      is_published: values.is_published,
      display_targets: values.display_targets,
    });

    if (uploadedFile && doc.id) {
      await attachFileMutation.mutateAsync({
        id: doc.id,
        data: { file_id: uploadedFile.fileId },
      });
    }

    handleClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent size="md">
        <SheetHeader>
          <SheetTitle>
            <Trans id="documents.create.title">Create Document</Trans>
          </SheetTitle>
          <SheetDescription>
            <Trans id="documents.create.description">
              Add a new document to your organization
            </Trans>
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <div>
              <p className="text-sm font-medium mb-1">
                <Trans id="documents.fields.version">Version</Trans>
              </p>
              <Badge variant="secondary">v1</Badge>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>
                    <Trans id="documents.fields.name">Name</Trans>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t({ id: "documents.placeholders.name", message: "Document name" })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans id="documents.fields.description">Description</Trans>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t({ id: "documents.placeholders.description", message: "Optional description" })}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>
                    <Trans id="documents.fields.category">Category</Trans>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t({ id: "documents.placeholders.category", message: "Select category" })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DOCUMENT_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {i18n._(DOCUMENT_CATEGORY_LABELS[cat.value])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confidentiality"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>
                    <Trans id="documents.fields.confidentiality">Confidentiality</Trans>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONFIDENTIALITY_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {i18n._(CONFIDENTIALITY_LABELS[level.value])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>
                      <Trans id="documents.fields.published">Published</Trans>
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="display_targets"
              render={() => (
                <FormItem>
                  <FormLabel>
                    <Trans id="documents.fields.displayTargets">Display Targets</Trans>
                  </FormLabel>
                  <div className="space-y-2">
                    {DISPLAY_TARGETS.map((target) => (
                      <FormField
                        key={target.value}
                        control={form.control}
                        name="display_targets"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(target.value)}
                                onCheckedChange={(checked) => {
                                  const current = field.value ?? [];
                                  if (checked) {
                                    field.onChange([...current, target.value]);
                                  } else {
                                    field.onChange(
                                      current.filter((v: string) => v !== target.value)
                                    );
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {i18n._(DISPLAY_TARGET_LABELS[target.value])}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DocumentFileSection
              uploadedFile={uploadedFile}
              onFileUploaded={setUploadedFile}
              onFileRemoved={handleFileRemoved}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                <Trans id="common.cancel">Cancel</Trans>
              </Button>
              <Button type="submit" disabled={createMutation.isPending || attachFileMutation.isPending}>
                {(createMutation.isPending || attachFileMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                <Trans id="common.create">Create</Trans>
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
